import React, { useState } from 'react';
import { Droplet, Clock, AlertCircle, Moon, BarChart, Calendar, Settings } from 'lucide-react';
import BathroomVisitAnalytics from './bathroom/BathroomVisitAnalytics';
import NighttimeVisitTracking from './bathroom/NighttimeVisitTracking';
import SlipDetectionMonitoring from './bathroom/SlipDetectionMonitoring';
import BathroomUsagePatterns from './bathroom/BathroomUsagePatterns';
import EmergencyResponseSettings from './bathroom/EmergencyResponseSettings';

export interface BathroomVisit {
  id: string;
  timestamp: Date;
  duration: number; // in seconds
  movementPattern: 'normal' | 'unstable' | 'rapid' | 'slow';
  pressureDistribution: 'balanced' | 'unbalanced' | 'shifting';
  waterDetected: boolean;
  exitStatus: 'normal' | 'assisted' | 'emergency';
  isNighttime: boolean;
}

export interface SlipEvent {
  id: string;
  timestamp: Date;
  severity: 'low' | 'medium' | 'high';
  responseTime: number; // in seconds
  hasAnomalousPressure: boolean;
  waterPresent: boolean;
  userRecovered: boolean;
  fallDetected: boolean;
}

export interface BathroomSafetyConfig {
  emergencyResponseActive: boolean;
  caregiverAlertThreshold: 'immediate' | 'delayed' | 'scheduledOnly';
  slipDetectionSensitivity: 'low' | 'medium' | 'high';
  waterAlertEnabled: boolean;
  extendedVisitThreshold: number; // in minutes
  nighttimeMonitoringEnabled: boolean;
  autoLightingEnabled: boolean;
}

const BathroomSafety: React.FC = () => {
  // Active view state
  const [activeView, setActiveView] = useState<string>('analytics');
  
  // Configuration state
  const [config, setConfig] = useState<BathroomSafetyConfig>({
    emergencyResponseActive: true,
    caregiverAlertThreshold: 'delayed',
    slipDetectionSensitivity: 'medium',
    waterAlertEnabled: true,
    extendedVisitThreshold: 20,
    nighttimeMonitoringEnabled: true,
    autoLightingEnabled: true
  });

  // Generate today's date and the previous week's date
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 7);

  // Format the date range for display
  const dateRangeText = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  // Render the appropriate component based on the active view
  const renderContent = () => {
    switch (activeView) {
      case 'analytics':
        return <BathroomVisitAnalytics />;
      case 'nighttime':
        return <NighttimeVisitTracking />;
      case 'slip':
        return <SlipDetectionMonitoring />;
      case 'patterns':
        return <BathroomUsagePatterns />;
      case 'settings':
        return <EmergencyResponseSettings config={config} setConfig={setConfig} />;
      default:
        return <BathroomVisitAnalytics />;
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Bathroom Safety Monitoring</h2>
          <p className="text-gray-600">Data for: {dateRangeText}</p>
        </div>
        <div className="flex space-x-2">
          <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
            <Clock className="inline-block mr-1 w-4 h-4" /> 
            4 visits today
          </div>
          <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
            <AlertCircle className="inline-block mr-1 w-4 h-4" /> 
            No alerts
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex overflow-x-auto mb-6 border-b">
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeView === 'analytics'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => setActiveView('analytics')}
        >
          <BarChart className="inline-block mr-2 w-5 h-5" />
          Visit Analytics
        </button>
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeView === 'nighttime'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => setActiveView('nighttime')}
        >
          <Moon className="inline-block mr-2 w-5 h-5" />
          Nighttime Visits
        </button>
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeView === 'slip'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => setActiveView('slip')}
        >
          <Droplet className="inline-block mr-2 w-5 h-5" />
          Slip Detection
        </button>
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeView === 'patterns'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => setActiveView('patterns')}
        >
          <Calendar className="inline-block mr-2 w-5 h-5" />
          Usage Patterns
        </button>
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeView === 'settings'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => setActiveView('settings')}
        >
          <Settings className="inline-block mr-2 w-5 h-5" />
          Alert Settings
        </button>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow">
        {renderContent()}
      </div>
    </div>
  );
};

export default BathroomSafety; 